import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import {
  fetchPendingErrandRiders,
  fetchPendingErrandWithdrawals,
} from '@/services/ant-design-pro/errand';
import '@ant-design/v5-patch-for-react-19';
import type { ProLayoutProps } from '@ant-design/pro-components';
import type { RuntimeConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { App, Badge, message as staticMessage } from 'antd';
import defaultSettings from '../config/defaultSettings';
import {
  buildLoginRedirectUrl,
  errorConfig,
  isAuthExpiredError,
} from './requestErrorConfig';
import { getApiBaseUrl } from './utils/runtimeConfig';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const EMPTY_ERRAND_PENDING_SUMMARY = {
  pendingRiderCount: 0,
  pendingWithdrawalCount: 0,
};

type AppRuntimeConfig = RuntimeConfig;
type AppLayoutConfig = NonNullable<AppRuntimeConfig['layout']>;
type LayoutInitData = Parameters<AppLayoutConfig>[0];
type ErrandPendingSummary = typeof EMPTY_ERRAND_PENDING_SUMMARY;

const hasPermission = (permissions: string[] | undefined, code: string) =>
  Boolean(permissions?.includes(code));

const loadErrandPendingSummary = async (
  permissions?: string[],
): Promise<ErrandPendingSummary> => {
  const summary = { ...EMPTY_ERRAND_PENDING_SUMMARY };

  if (!permissions?.length) {
    return summary;
  }

  const tasks: Array<Promise<void>> = [];

  if (hasPermission(permissions, 'errand:rider:list')) {
    tasks.push(
      fetchPendingErrandRiders()
        .then((res) => {
          if (res.success && res.data) {
            summary.pendingRiderCount = Number(
              res.data.total || res.data.items?.length || 0,
            );
          }
        })
        .catch(() => undefined),
    );
  }

  if (hasPermission(permissions, 'errand:withdrawal:list')) {
    tasks.push(
      fetchPendingErrandWithdrawals()
        .then((res) => {
          if (res.success && res.data) {
            const payload = res.data as {
              items?: API.ErrandWithdrawal[];
              total?: number;
            };

            summary.pendingWithdrawalCount = Number(
              payload.total || payload.items?.length || 0,
            );
          }
        })
        .catch(() => undefined),
    );
  }

  await Promise.all(tasks);
  return summary;
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  errandPendingSummary: ErrandPendingSummary;
  refreshErrandPendingSummary?: () => Promise<ErrandPendingSummary>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (error) {
      if (isAuthExpiredError(error)) {
        localStorage.removeItem('token');
        history.push(
          buildLoginRedirectUrl(
            window.location.pathname,
            window.location.search,
          ),
        );
      } else {
        const backendMsg = (
          error as { response?: { data?: { message?: string } } }
        )?.response?.data?.message;
        staticMessage.error(backendMsg || '获取用户信息失败，请刷新重试');
      }
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { pathname } = window.location;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(pathname)
  ) {
    const currentUser = await fetchUserInfo();
    const refreshErrandPendingSummary = async () =>
      loadErrandPendingSummary(currentUser?.permissions);
    const errandPendingSummary = await refreshErrandPendingSummary();

    return {
      fetchUserInfo,
      currentUser,
      errandPendingSummary,
      refreshErrandPendingSummary,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    errandPendingSummary: { ...EMPTY_ERRAND_PENDING_SUMMARY },
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: AppLayoutConfig = ({
  initialState,
  setInitialState,
}: LayoutInitData) => {
  return {
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_: unknown, avatarChildren: React.ReactNode) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { pathname } = window.location;
      // 如果没有登录，就重定向到 login
      if (
        !initialState?.currentUser &&
        pathname !== loginPath &&
        !localStorage.getItem('token')
      ) {
        history.push(buildLoginRedirectUrl(pathname, window.location.search));
        return;
      }

      if (
        pathname.startsWith('/errand') &&
        initialState?.refreshErrandPendingSummary
      ) {
        initialState
          .refreshErrandPendingSummary()
          .then((summary: ErrandPendingSummary) => {
            setInitialState(
              (prevInitialState: LayoutInitData['initialState']) =>
                prevInitialState
                  ? {
                      ...prevInitialState,
                      errandPendingSummary: summary,
                    }
                  : prevInitialState,
            );
          });
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    menuItemRender: (
      item: { path?: string; isUrl?: boolean; target?: string },
      defaultDom: React.ReactNode,
    ) => {
      if (!defaultDom) {
        return defaultDom;
      }

      const pendingCount =
        item.path === '/errand/riders'
          ? Number(initialState?.errandPendingSummary?.pendingRiderCount || 0)
          : item.path === '/errand/withdrawals'
            ? Number(
                initialState?.errandPendingSummary?.pendingWithdrawalCount || 0,
              )
            : 0;

      const menuContent =
        pendingCount > 0 ? (
          <Badge
            count={pendingCount}
            overflowCount={99}
            size="small"
            offset={[12, 0]}
            style={{
              backgroundColor:
                item.path === '/errand/riders' ? '#fa8c16' : '#1677ff',
            }}
          >
            {defaultDom}
          </Badge>
        ) : (
          defaultDom
        );

      if (item.isUrl && item.path) {
        return (
          <a href={item.path} target={item.target} rel="noreferrer">
            {menuContent}
          </a>
        );
      }

      if (!item.path) {
        return menuContent;
      }

      return (
        <Link to={item.path} target={item.target}>
          {menuContent}
        </Link>
      );
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children: React.ReactElement, _props: ProLayoutProps) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <App>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState(
                  (preInitialState: LayoutInitData['initialState']) =>
                    preInitialState
                      ? {
                          ...preInitialState,
                          settings,
                        }
                      : {
                          settings,
                          errandPendingSummary: {
                            ...EMPTY_ERRAND_PENDING_SUMMARY,
                          },
                        },
                );
              }}
            />
          )}
        </App>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: AppRuntimeConfig['request'] = {
  // 开发环境和测试环境默认走代理，生产环境通过构建参数注入实际地址。
  baseURL: getApiBaseUrl(),
  ...errorConfig,
};
